// src/components/planner/modals/EditSubjectModal.tsx

import React, { useState } from 'react';
import { X, Save, Trash2 } from 'lucide-react';

const API_BASE_URL = '/api';

interface EditSubjectModalProps {
    subject: {
        _id: string;
        name: string;
        priority: string;
        status: string;
        targetMinutes?: number;
        weight?: number;
        deadline?: string;
        notes?: string;
    };
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const EditSubjectModal: React.FC<EditSubjectModalProps> = ({
    subject,
    onClose,
    onSave,
    onShowToast
}) => {
    const [formData, setFormData] = useState({
        name: subject.name,
        priority: subject.priority,
        status: subject.status,
        targetMinutes: subject.targetMinutes?.toString() || '',
        weight: subject.weight ?? 5,
        deadline: subject.deadline
            ? new Date(subject.deadline).toISOString().split('T')[0]
            : '',
        notes: subject.notes || ''
    });

    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            onShowToast('Preencha o nome do assunto', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const payload = {
                ...formData,
                targetMinutes: formData.targetMinutes ? parseInt(formData.targetMinutes) : undefined,
                deadline: formData.deadline || undefined
            };

            const response = await fetch(`${API_BASE_URL}/planner/subjects/${subject._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                onShowToast('Assunto atualizado!', 'success');
                onSave();
            } else {
                onShowToast('Erro ao atualizar assunto', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar assunto', 'error');
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/subjects/${subject._id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Assunto arquivado', 'info');
                onSave();
            } else {
                onShowToast('Erro ao arquivar assunto', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao arquivar assunto', 'error');
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal plnr-modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Editar Assunto</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">
                        <div className="plnr-form-group">
                            <label>Nome do assunto *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Cardiologia"
                                className="plnr-input"
                                required
                            />
                        </div>

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Prioridade</label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className="plnr-input"
                                >
                                    <option value="high">Alta</option>
                                    <option value="medium">Média</option>
                                    <option value="low">Baixa</option>
                                </select>
                            </div>

                            <div className="plnr-form-group">
                                <label>Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="plnr-input"
                                >
                                    <option value="not-started">Não iniciado</option>
                                    <option value="in-progress">Em andamento</option>
                                    <option value="completed">Concluído</option>
                                    <option value="reviewing">Em revisão</option>
                                </select>
                            </div>
                        </div>

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Meta de tempo (minutos)</label>
                                <input
                                    type="number"
                                    value={formData.targetMinutes}
                                    onChange={(e) => setFormData({ ...formData, targetMinutes: e.target.value })}
                                    placeholder="Ex: 300"
                                    className="plnr-input"
                                    min="0"
                                />
                            </div>

                            <div className="plnr-form-group">
                                <label>Peso (1-10)</label>
                                <input
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) })}
                                    className="plnr-input"
                                    min="1"
                                    max="10"
                                />
                            </div>
                        </div>

                        <div className="plnr-form-group">
                            <label>Prazo (opcional)</label>
                            <input
                                type="date"
                                value={formData.deadline}
                                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                className="plnr-input"
                            />
                        </div>

                        <div className="plnr-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Observações sobre este assunto..."
                                className="plnr-textarea"
                                rows={3}
                            />
                        </div>

                        {/* CONFIRMAÇÃO DE EXCLUSÃO */}
                        {confirmDelete && (
                            <div style={{
                                padding: '12px',
                                background: 'rgba(220, 38, 38, 0.08)',
                                border: '1px solid rgba(220, 38, 38, 0.3)',
                                borderRadius: '8px',
                            }}>
                                <p style={{ fontSize: '13px', color: '#dc2626', margin: '0 0 10px 0', fontWeight: 600 }}>
                                    Deletar este assunto? Ele não aparecerá mais na lista.
                                </p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button" className="plnr-btn danger" onClick={handleDelete}>
                                        <Trash2 size={14} />
                                        Sim, arquivar
                                    </button>
                                    <button type="button" className="plnr-btn secondary" onClick={() => setConfirmDelete(false)}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="plnr-modal-footer" style={{ justifyContent: 'space-between' }}>
                        <button
                            type="button"
                            className="plnr-btn danger"
                            onClick={() => setConfirmDelete(true)}
                            style={{ opacity: confirmDelete ? 0.4 : 1 }}
                            disabled={confirmDelete}
                        >
                            <Trash2 size={14} />
                            Deletar
                        </button>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" className="plnr-btn secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button type="submit" className="plnr-btn primary">
                                <Save size={16} />
                                Salvar
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditSubjectModal;